/**
 * «Whose turn» on the map (initiative brief 25.10, Р5): the ring and the «ходит» / «твой ход» tag on the token that
 * acts, the hover ring when a tracker row is hovered, and a short flash after «Показать на карте». The tracker window
 * and the canvases live in different subtrees — this tiny store carries the hover / flash between them; the turn itself
 * the canvases read from InitiativeContext.
 */
type Listener = () => void;

let hover: string | null = null;
let flash: string | null = null;
let flashTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<Listener>();

function emit(): void { for (const l of listeners) l(); }

export function setInitiativeHover(tokenId: string | null): void {
  if (hover === tokenId) return;
  hover = tokenId;
  emit();
}

export function flashInitiativeToken(tokenId: string): void {
  flash = tokenId;
  if (flashTimer) clearTimeout(flashTimer);
  flashTimer = setTimeout(() => { flash = null; emit(); }, 1600);
  emit();
}

export function initiativeHoverState(): { hover: string | null; flash: string | null } {
  return { hover, flash };
}

export function subscribeInitiativeHighlight(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

let accentCache: { at: number; color: string } | null = null;
/** The table accent (the same colour as the current row and the portraits' turn glow). */
export function tableAccent(): string {
  const now = Date.now();
  if (accentCache && now - accentCache.at < 2000) return accentCache.color;
  let color = '';
  try { color = getComputedStyle(document.documentElement).getPropertyValue('--color-hl').trim(); } catch { /* no DOM */ }
  if (!color || color.startsWith('var(')) color = '#d9455f';
  accentCache = { at: now, color };
  return color;
}

/**
 * Draw the turn ring (solid accent + dashed outer ring + the tag above) or the hover ring around a token whose centre
 * is (cx, cy) and radius r, in screen pixels.
 */
export function drawInitiativeRing(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, kind: 'turn' | 'hover', label?: string): void {
  ctx.save();
  if (kind === 'hover') {
    ctx.strokeStyle = 'rgba(163, 161, 168, 0.95)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(cx, cy, r + 3, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    return;
  }
  const accent = tableAccent();
  ctx.strokeStyle = accent;
  ctx.lineWidth = 3;
  ctx.shadowColor = accent;
  ctx.shadowBlur = 14;
  ctx.beginPath();
  ctx.arc(cx, cy, r + 3, 0, Math.PI * 2);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.globalAlpha = 0.7;
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 4]);
  ctx.beginPath();
  ctx.arc(cx, cy, r + 9, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
  if (label) {
    ctx.font = '600 10.5px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
    const w = ctx.measureText(label).width + 12;
    const h = 16;
    const x = cx - w / 2;
    const y = cy - r - 12 - h;
    ctx.fillStyle = accent;
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, w, h, 8); else ctx.rect(x, y, w, h);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, cx, y + h / 2 + 0.5);
  }
  ctx.restore();
}
